import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from '@tanstack/react-router'
import { CalendarClock, Clapperboard, Globe2, Info, Radio, Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { notifyError } from '@/components/notify'
import { FeatureHint } from '@/components/plan-upsell'
import { QueryError } from '@/components/query-state'
import { TimezoneCombobox } from '@/components/timezone-combobox'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Field, FormError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CampaignSelect } from '@/features/campaigns/components/campaign-select'
import { ConditionField } from '@/features/rules/components/condition-field'
import { useFeature, usePermission } from '@/hooks/use-permission'
import { isApiError, isProblem } from '@/lib/api'
import { cn } from '@/lib/utils'
import { useScheduleEditor } from '@/stores/schedule-editor'
import { SCHEDULE_RECURRENCES, type Schedule, type ScheduleRecurrence } from '@/types/api'
import { useCreateSchedule, useSchedule, useUpdateSchedule } from '../api'
import { overnightHint, RECURRENCE_LABELS, ruleSummary } from '../lib/rule'
import {
  DEFAULT_PRIORITY,
  mapScheduleErrors,
  scheduleFormDefaults,
  scheduleFormSchema,
  scheduleFormToPayload,
  type SchedulePrefill,
  type ScheduleFormValues,
} from '../lib/schedule-form'
import { ContentPicker, TargetsPicker, targetLabel, useTargetOptions } from './pickers'
import { DaysOfMonthPicker, WeekdayToggles } from './rule-inputs'
import { DeleteScheduleDialog } from './delete-schedule-dialog'
import { ScheduleStatusBadge } from './schedule-status-badge'

const FORM_ID = 'schedule-editor-form'

/** Global create / edit sheet (opened through `useScheduleEditor`). */
export function ScheduleEditorSheet() {
  const { open, scheduleId, prefill, session, close } = useScheduleEditor()
  const schedule = useSchedule(open ? scheduleId : null)
  const canManage = usePermission('schedules.manage')
  const [pending, setPending] = useState(false)
  const [deleting, setDeleting] = useState<Schedule | null>(null)
  const editing = !!scheduleId
  const readOnly = !canManage

  return (
    <Sheet open={open} onOpenChange={(o) => !o && close()}>
      <SheetContent className="sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {editing ? (readOnly ? 'Schedule' : 'Edit schedule') : 'New schedule'}
            {schedule.data && <ScheduleStatusBadge status={schedule.data.status} />}
          </SheetTitle>
          <SheetDescription>{editing ? (schedule.data?.name ?? 'Loading…') : 'Play a channel or slideshow on chosen screens at chosen times.'}</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {open &&
            (editing && schedule.isPending ? (
              <EditorSkeleton />
            ) : editing && schedule.isError ? (
              <QueryError error={schedule.error} onRetry={() => void schedule.refetch()} />
            ) : (
              <ScheduleForm
                key={`${session}:${schedule.data?.id ?? 'new'}`}
                schedule={editing ? (schedule.data ?? null) : null}
                prefill={prefill}
                readOnly={readOnly}
                onPendingChange={setPending}
                onSaved={close}
              />
            ))}
        </SheetBody>
        <SheetFooter className="justify-between">
          <div>
            {editing && canManage && schedule.data && (
              <Button variant="destructive-ghost" onClick={() => setDeleting(schedule.data)}>
                <Trash2 /> Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={close}>
              {readOnly ? 'Close' : 'Cancel'}
            </Button>
            {!readOnly && (
              <Button type="submit" form={FORM_ID} loading={pending} disabled={editing && !schedule.data}>
                {editing ? 'Save changes' : 'Create schedule'}
              </Button>
            )}
          </div>
        </SheetFooter>
      </SheetContent>
      <DeleteScheduleDialog schedule={deleting} onOpenChange={(o) => !o && setDeleting(null)} onDeleted={close} />
    </Sheet>
  )
}

function ScheduleForm({
  schedule,
  prefill,
  readOnly,
  onPendingChange,
  onSaved,
}: {
  schedule: Schedule | null
  prefill: SchedulePrefill
  readOnly: boolean
  onPendingChange: (pending: boolean) => void
  onSaved: (s: Schedule) => void
}) {
  const advanced = useFeature('advanced_scheduling')
  const hasCampaigns = useFeature('campaigns')
  const create = useCreateSchedule()
  const update = useUpdateSchedule()
  const targetOptions = useTargetOptions()
  const form = useForm<ScheduleFormValues>({ resolver: zodResolver(scheduleFormSchema), defaultValues: scheduleFormDefaults(schedule, prefill) })
  const { errors } = form.formState
  const v = useWatch({ control: form.control }) as ScheduleFormValues
  const payloadPreview = scheduleFormToPayload(v)

  const onSubmit = form.handleSubmit(async (values) => {
    const input = scheduleFormToPayload(values)
    onPendingChange(true)
    try {
      const saved = schedule ? await update.mutateAsync({ id: schedule.id, ...input }) : await create.mutateAsync(input)
      toast.success(schedule ? 'Schedule updated' : 'Schedule created', { description: `${saved.name} — screens pick it up at their next sync.` })
      onSaved(saved)
    } catch (e) {
      if (isApiError(e) && e.isValidation) {
        const { fields, unmatched } = mapScheduleErrors(e.errors, input.targets, (type, id) => targetLabel(targetOptions, type, id))
        let first = true
        for (const [field, message] of Object.entries(fields)) {
          form.setError(field as keyof ScheduleFormValues, { type: 'server', message }, { shouldFocus: first })
          first = false
        }
        if (unmatched.length > 0 || Object.keys(fields).length === 0) form.setError('root.server', { type: 'server', message: unmatched[0] ?? e.detail })
      } else {
        if (isProblem(e, 'feature-unavailable')) form.setError('root.server', { type: 'server', message: 'Part of this schedule needs a higher plan (see the highlighted options).' })
        notifyError(e)
      }
    } finally {
      onPendingChange(false)
    }
  })

  const recurrence = v.recurrence
  const hint = !v.all_day ? overnightHint(v.start_time, v.until_midnight ? '24:00' : v.end_time) : null

  return (
    <form id={FORM_ID} onSubmit={onSubmit} noValidate>
      <fieldset disabled={readOnly} className="grid gap-6">
        <FormError message={errors.root?.server?.message} />

        <SummaryCard text={ruleSummary(payloadPreview)} enabled={v.is_enabled} />

        <Section title="What plays">
          <Field label="Name" required error={errors.name?.message}>
            <Input autoFocus={!schedule} placeholder="e.g. Lunch menu weekdays" {...form.register('name')} />
          </Field>
          <Controller
            control={form.control}
            name="content_type"
            render={({ field }) => (
              <div className="grid gap-1.5">
                <span className="text-[13px] font-medium text-foreground/90" id="content-type-label">
                  Content
                </span>
                <Tabs
                  value={field.value}
                  onValueChange={(t) => {
                    field.onChange(t)
                    form.setValue('content_id', '')
                  }}
                >
                  <TabsList aria-labelledby="content-type-label">
                    <TabsTrigger value="channel" disabled={readOnly}>
                      <Radio /> Channel
                    </TabsTrigger>
                    <TabsTrigger value="slideshow" disabled={readOnly}>
                      <Clapperboard /> Slideshow
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            )}
          />
          <Controller
            control={form.control}
            name="content_id"
            render={({ field }) => (
              <Field label={v.content_type === 'channel' ? 'Channel' : 'Slideshow'} required error={errors.content_id?.message} hint={v.content_type === 'slideshow' ? 'Only published slideshows can be scheduled.' : undefined}>
                <ContentPicker type={v.content_type} value={field.value} onChange={field.onChange} disabled={readOnly} />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="campaign_id"
            render={({ field }) => (
              <Field label="Campaign" optional error={errors.campaign_id?.message} hint={!hasCampaigns ? undefined : 'Campaign dates clip this schedule; it only plays while the campaign is active.'}>
                <CampaignSelect value={field.value} onChange={field.onChange} disabled={readOnly || (!hasCampaigns && !field.value)} />
              </Field>
            )}
          />
          {!hasCampaigns && <FeatureHint>Campaigns aren’t included in your plan.</FeatureHint>}
        </Section>

        <Section title="Where">
          <Controller
            control={form.control}
            name="targets"
            render={({ field }) => (
              <Field label="Targets" required error={errors.targets?.message as string | undefined} hint="When several targets match a display, the most specific wins: display > location > group / tag > all displays.">
                <TargetsPicker value={field.value} onChange={field.onChange} options={targetOptions} />
              </Field>
            )}
          />
        </Section>

        <Section title="When">
          <Controller
            control={form.control}
            name="recurrence"
            render={({ field }) => (
              <Field label="Repeats" error={errors.recurrence?.message}>
                <RecurrenceSelect value={field.value} onChange={field.onChange} advanced={advanced} />
              </Field>
            )}
          />
          {!advanced && <FeatureHint>Day-of-month rules need Advanced scheduling.</FeatureHint>}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={recurrence === 'once' ? 'Date' : 'Start date'} required={recurrence === 'once'} optional={recurrence !== 'once'} error={errors.start_date?.message}>
              <Input type="date" {...form.register('start_date')} />
            </Field>
            {recurrence !== 'once' && (
              <Field label="End date" optional error={errors.end_date?.message} hint="Inclusive — an overnight window starting on it runs into the next day.">
                <Input type="date" {...form.register('end_date')} />
              </Field>
            )}
          </div>

          <div className="grid gap-3">
            <div className="flex items-center gap-2">
              <Controller
                control={form.control}
                name="all_day"
                render={({ field }) => <Switch id="schedule-all-day" checked={field.value} onCheckedChange={field.onChange} disabled={readOnly} />}
              />
              <Label htmlFor="schedule-all-day">All day</Label>
            </div>
            {!v.all_day && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Start time" required error={errors.start_time?.message}>
                  <Input type="time" step={60} {...form.register('start_time')} />
                </Field>
                <div className="grid gap-1.5">
                  <Field label="End time" required error={errors.end_time?.message} hint="Exclusive. Earlier than the start = ends the next day.">
                    <Input type="time" step={60} disabled={v.until_midnight} {...form.register('end_time')} />
                  </Field>
                  <div className="flex items-center gap-2">
                    <Controller
                      control={form.control}
                      name="until_midnight"
                      render={({ field }) => <Checkbox id="schedule-midnight" checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} disabled={readOnly} />}
                    />
                    <Label htmlFor="schedule-midnight" className="font-normal">
                      Until midnight (24:00)
                    </Label>
                  </div>
                </div>
              </div>
            )}
            {hint && (
              <p className="flex items-start gap-1.5 rounded-lg border border-info/30 bg-info/8 px-3 py-2 text-[12.5px]">
                <Info className="mt-0.5 size-3.5 shrink-0 text-info" /> {hint}
              </p>
            )}
          </div>

          {recurrence !== 'once' && (
            <Controller
              control={form.control}
              name="weekdays"
              render={({ field }) => (
                <Field label={recurrence === 'monthly_dom' ? 'Only on these weekdays' : 'Days of the week'} error={errors.weekdays?.message}>
                  <WeekdayToggles value={field.value} onChange={field.onChange} disabled={readOnly} />
                </Field>
              )}
            />
          )}
          {recurrence === 'monthly_dom' && (
            <Controller
              control={form.control}
              name="days_of_month"
              render={({ field }) => (
                <Field label="Days of the month" required error={errors.days_of_month?.message} hint="Days a month doesn’t have (e.g. 31 in April) are skipped, never moved.">
                  <DaysOfMonthPicker value={field.value} onChange={field.onChange} disabled={readOnly} />
                </Field>
              )}
            />
          )}

          <Controller
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <div className="grid gap-1.5">
                <TimezoneChoice value={field.value} onChange={field.onChange} advanced={advanced} disabled={readOnly} error={errors.timezone?.message} />
              </div>
            )}
          />
        </Section>

        <Section title="Condition">
          <Controller
            control={form.control}
            name="condition_rule_id"
            render={({ field }) => (
              <div className="grid gap-1.5">
                <ConditionField
                  value={{ rule_id: field.value, unknown_as: v.condition_unknown_as }}
                  current={schedule?.condition ?? null}
                  disabled={readOnly}
                  subject="this schedule"
                  onChange={(c) => {
                    field.onChange(c.rule_id)
                    form.setValue('condition_unknown_as', c.unknown_as, { shouldDirty: true })
                  }}
                />
                {errors.condition_rule_id?.message && (
                  <p role="alert" className="text-[12.5px] font-medium text-destructive">
                    {errors.condition_rule_id.message}
                  </p>
                )}
              </div>
            )}
          />
        </Section>

        <Section title="Priority & validity">
          <Controller
            control={form.control}
            name="priority"
            render={({ field }) => (
              <PriorityField value={field.value} onChange={field.onChange} advanced={advanced} disabled={readOnly} error={errors.priority?.message} />
            )}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Valid from" optional error={errors.valid_from?.message} hint="Your browser’s time.">
              <Input type="datetime-local" {...form.register('valid_from')} />
            </Field>
            <Field label="Valid until" optional error={errors.valid_until?.message} hint="Exclusive.">
              <Input type="datetime-local" {...form.register('valid_until')} />
            </Field>
          </div>
          <div className="flex items-start gap-3 rounded-lg border px-3 py-2.5">
            <Controller
              control={form.control}
              name="is_enabled"
              render={({ field }) => <Switch id="schedule-enabled" checked={field.value} onCheckedChange={field.onChange} disabled={readOnly} className="mt-0.5" />}
            />
            <div>
              <Label htmlFor="schedule-enabled">Enabled</Label>
              <p className="mt-1 text-[12.5px] text-muted-foreground">Turn off to keep the schedule without playing it.</p>
            </div>
          </div>
        </Section>

        {schedule && (
          <p className="text-xs text-muted-foreground">
            Version {schedule.version}. Check what a screen shows at any moment with the simulator on its{' '}
            <Link to="/displays" className="font-medium text-primary hover:underline">
              display page
            </Link>
            .
          </p>
        )}
      </fieldset>
    </form>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="grid gap-4">
      <h3 className="text-[11.5px] font-semibold tracking-[0.1em] text-muted-foreground uppercase">{title}</h3>
      {children}
    </section>
  )
}

function SummaryCard({ text, enabled }: { text: string; enabled: boolean }) {
  return (
    <div className={cn('flex items-start gap-3 rounded-xl border bg-[linear-gradient(135deg,color-mix(in_oklch,var(--primary)_8%,var(--card)),var(--card))] p-3.5', !enabled && 'opacity-70')} aria-live="polite">
      <CalendarClock className="mt-0.5 size-4 shrink-0 text-primary" />
      <div className="min-w-0">
        <p className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">Rule</p>
        <p className="text-sm font-medium">{text}</p>
        {!enabled && <p className="text-xs text-muted-foreground">Disabled — it won’t play.</p>}
      </div>
    </div>
  )
}

function RecurrenceSelect({ value, onChange, advanced, id, ...aria }: { value: ScheduleRecurrence; onChange: (v: ScheduleRecurrence) => void; advanced: boolean; id?: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) {
  return (
    <Select value={value} onValueChange={(r) => onChange(r as ScheduleRecurrence)}>
      <SelectTrigger id={id} {...aria}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {SCHEDULE_RECURRENCES.map((r) => (
          <SelectItem key={r} value={r} disabled={r === 'monthly_dom' && !advanced && value !== r}>
            {RECURRENCE_LABELS[r]}
            {r === 'monthly_dom' && !advanced ? ' (upgrade)' : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function TimezoneChoice({ value, onChange, advanced, disabled, error }: { value: string; onChange: (v: string) => void; advanced: boolean; disabled?: boolean; error?: string }) {
  const fixed = value !== 'display'
  return (
    <div className="grid gap-2">
      <span className="text-[13px] font-medium text-foreground/90">Time zone</span>
      <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Time zone">
        {(
          [
            { key: false, label: 'Display’s local time', text: 'Each screen reads the times in its own zone.', icon: CalendarClock },
            { key: true, label: 'Fixed time zone', text: 'Same instant everywhere.', icon: Globe2 },
          ] as const
        ).map((o) => (
          <button
            key={String(o.key)}
            type="button"
            role="radio"
            aria-checked={fixed === o.key}
            disabled={disabled || (o.key && !advanced && !fixed)}
            onClick={() => onChange(o.key ? 'UTC' : 'display')}
            className={cn(
              'flex items-start gap-2.5 rounded-lg border p-3 text-left transition outline-none focus-visible:ring-[3px] focus-visible:ring-ring disabled:opacity-50',
              fixed === o.key ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'hover:bg-muted/50',
            )}
          >
            <o.icon className="mt-0.5 size-4 shrink-0 text-primary" />
            <span>
              <span className="block text-[13px] font-medium">{o.label}</span>
              <span className="block text-xs text-muted-foreground">{o.text}</span>
            </span>
          </button>
        ))}
      </div>
      {fixed && (
        <Field label="Zone" error={error}>
          <TimezoneCombobox value={value} onChange={onChange} disabled={disabled || !advanced} />
        </Field>
      )}
      {!advanced && <FeatureHint>Fixed time zones need Advanced scheduling.</FeatureHint>}
    </div>
  )
}

function PriorityField({ value, onChange, advanced, disabled, error }: { value: number; onChange: (v: number) => void; advanced: boolean; disabled?: boolean; error?: string }) {
  const locked = !advanced && value === DEFAULT_PRIORITY
  return (
    <div className="grid gap-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor="schedule-priority">Priority</Label>
        <span className="text-sm font-semibold tabular">{value}</span>
      </div>
      <input
        id="schedule-priority"
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        disabled={disabled || locked}
        aria-describedby="schedule-priority-hint"
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--primary)] disabled:opacity-50"
      />
      <p id="schedule-priority-hint" className={cn('text-[12.5px]', error ? 'font-medium text-destructive' : 'text-muted-foreground')}>
        {error ?? 'Among schedules of the same targeting class, the higher priority wins; equal priorities share airtime.'}
      </p>
      {!advanced && (
        <div className="flex items-center gap-3">
          <FeatureHint>Priorities other than 50 need Advanced scheduling.</FeatureHint>
          {value !== DEFAULT_PRIORITY && (
            <Button type="button" variant="link" size="sm" onClick={() => onChange(DEFAULT_PRIORITY)}>
              Reset to 50
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function EditorSkeleton() {
  return (
    <div className="grid gap-4">
      <Skeleton className="h-16 rounded-xl" />
      <Skeleton className="h-9" />
      <Skeleton className="h-9" />
      <Skeleton className="h-32" />
      <Skeleton className="h-48" />
    </div>
  )
}
